import { Lesson } from '@/models/lesson';
import { parseSlide } from '@/types/slide';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import {
    CREATE_LESSON,
    CreateLessonResponse,
    CreateLessonVariables,
    GET_LESSONS,
    GET_LESSONS_BY_FOLDERS,
    GET_LESSONS_COUNT,
    GET_LESSON_SLIDES,
    GetLessonSlidesResponse,
    GetLessonsByFoldersResponse,
    GetLessonsCountResponse,
    GetLessonsResponse,
    UPDATE_LESSONS,
    UPDATE_LESSON_VERSION,
    UpdateLessonVariables,
    UpdateLessonVersionResponse,
    UpdateLessonVersionVariables,
} from './gql';

export const useLessons = (filters?: unknown, page?: { size: number; index: number }) => {
    // ui state
    const { data, loading, refetch } = useQuery<GetLessonsResponse>(GET_LESSONS, {
        variables: {
            skip: page ? page.index * page.size : undefined,
            take: page ? page.size : undefined,
            where: filters,
        },
        fetchPolicy: 'network-only',
    });
    const [getLessonSlides] = useLazyQuery<GetLessonSlidesResponse>(GET_LESSON_SLIDES, { fetchPolicy: 'network-only' });
    const [lessons, setLessons] = useState<Lesson[] | undefined>(undefined);

    // gql
    const [getLessonsIds] = useLazyQuery<GetLessonsByFoldersResponse>(GET_LESSONS_BY_FOLDERS, { fetchPolicy: 'network-only' });
    const [createLesson] = useMutation<CreateLessonResponse, CreateLessonVariables>(CREATE_LESSON);
    const [updateLessonVersion] = useMutation<UpdateLessonVersionResponse, UpdateLessonVersionVariables>(UPDATE_LESSON_VERSION);
    const [updateLessons] = useMutation<CreateLessonResponse, UpdateLessonVariables>(UPDATE_LESSONS);
    const { data: lessonsCountResponse, refetch: refetchLessonsCount } = useQuery<GetLessonsCountResponse>(GET_LESSONS_COUNT, {
        variables: {
            where: filters,
        },
        fetchPolicy: 'network-only',
    });

    const create = useCallback(
        async (folderId: string, name: string): Promise<Lesson> => {
            try {
                const createResponse = await createLesson({
                    variables: {
                        lessonCreateInput: {
                            ...(folderId
                                ? {
                                      folder: {
                                          connect: {
                                              id: folderId,
                                          },
                                      },
                                  }
                                : {}),
                        },
                    },
                });
                if (createResponse.data?.createLesson) {
                    await Promise.allSettled([refetchLessonsCount()]);

                    const updateResponse = await updateLessonVersion({ variables: { where: { id: createResponse.data.createLesson.latestLessonDraft.id }, data: { lessonName: name } } });
                    if (updateResponse.data?.updateLessonVersion) {
                        return new Lesson({ ...createResponse.data.createLesson, latestLessonDraft: { ...createResponse.data.createLesson.latestLessonDraft, lessonName: name, slides: [] } });
                    }
                }
            } catch (error) {
                return Promise.reject(new Error(`Failed to create lesson: ${error}`));
            }

            return Promise.reject(new Error('Failed to create lesson: missing response'));
        },
        [createLesson, refetchLessonsCount, updateLessonVersion],
    );

    const update = useCallback(
        async (lesson: Lesson, option: { name?: string }) => {
            try {
                await updateLessonVersion({ variables: { where: { id: lesson.latestLessonDraft.id }, data: { lessonName: option.name } } });
                return new Lesson({
                    ...lesson,
                    latestLessonDraft: {
                        ...lesson.latestLessonDraft,
                        lessonName: option.name ?? lesson.latestLessonDraft.lessonName,
                        dateUpdated: new Date().toISOString(),
                    },
                });
            } catch (error) {
                return Promise.reject(new Error(`Failed to update lesson version: ${error}`));
            }
        },
        [updateLessonVersion],
    );

    const move = useCallback(
        async (lessonId: string, folderId: string | null) => {
            if (folderId && lessonId === folderId) {
                return;
            }

            try {
                await updateLessons({
                    variables: {
                        data: [
                            {
                                where: {
                                    id: lessonId,
                                },
                                data: {
                                    folder: folderId
                                        ? {
                                              connect: { id: folderId },
                                          }
                                        : {
                                              disconnect: true,
                                          },
                                },
                            },
                        ],
                    },
                });
            } catch (error) {
                return Promise.reject(new Error(`Failed to update lesson version: ${error}`));
            }
        },
        [updateLessons],
    );

    const remove = useCallback(
        async (lessonIds: string[]) => {
            if (lessonIds.length === 0) {
                return;
            }

            try {
                await updateLessons({
                    variables: {
                        data: lessonIds.map((id) => ({ where: { id }, data: { archived: true } })),
                    },
                });
                await Promise.allSettled([refetchLessonsCount()]);
            } catch (error) {
                return Promise.reject(new Error(`Failed to update lesson: ${error}`));
            }
        },
        [refetchLessonsCount, updateLessons],
    );

    const getLessonsByFolders = useCallback(
        async (ids: string[]) => {
            const response = await getLessonsIds({
                variables: {
                    where: {
                        folder: { id: { in: ids } },
                        archived: { equals: false },
                    },
                },
            });

            return response.data?.lessons ?? [];
        },
        [getLessonsIds],
    );

    useEffect(() => {
        (async () => {
            if (data && !loading) {
                const allSlides = await getLessonSlides({
                    variables: {
                        where: {
                            lessonVersion: {
                                id: { in: data.lessons.filter((lesson) => lesson.latestLessonDraft != null).map((lesson) => lesson.latestLessonDraft.id) },
                            },
                            sort: { equals: 0 },
                        },
                    },
                });

                // parse lessons
                const lessons: Lesson[] = [];
                for (const lesson of data.lessons) {
                    if (lesson.latestLessonDraft == null) {
                        continue;
                    }

                    // get the first slide for the lesson
                    const slidesForLesson = allSlides.data?.slides.filter((slide) => slide.lessonVersion.id === lesson.latestLessonDraft.id) ?? [];

                    lessons.push(
                        new Lesson({
                            ...lesson,
                            latestLessonDraft: {
                                ...lesson.latestLessonDraft,
                                slides: slidesForLesson.map((slide) => parseSlide(slide)),
                            },
                        }),
                    );
                }
                setLessons(lessons);
            }
        })();
    }, [data, getLessonSlides, loading]);

    return {
        lessons,
        loading,
        create,
        update,
        move,
        remove,
        refetch,
        total: lessonsCountResponse?.lessonsCount,
        getLessonsByFolders,
    };
};
